import { api } from './client';

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }).then((r) => r.data),
  register: (payload: { email: string; password: string; fullName: string; businessName: string; currency?: string }) =>
    api.post('/auth/register', payload).then((r) => r.data),
  logout: (refreshToken?: string) =>
    api.post('/auth/logout', { refreshToken }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  permissions: () => api.get('/auth/permissions').then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),
};

export const posApi = {
  getCurrentShift: () => api.get('/pos/shifts/current').then((r) => r.data),
  openShift: (terminalCode: string, openingFloat: number) =>
    api.post('/pos/shifts/open', { terminalCode, openingFloat }).then((r) => r.data),
  closeShift: (actualCash: number, cashBreakdown?: Record<string, number>, notes?: string) =>
    api.post('/pos/shifts/close', { actualCash, cashBreakdown, notes }).then((r) => r.data),
  lookupProduct: (q: string) =>
    api.get('/pos/lookup/product', { params: { q } }).then((r) => r.data.data),
  createSale: (payload: any) =>
    api.post('/pos/sales', payload).then((r) => r.data),
  getSale: (id: string) => api.get(`/pos/sales/${id}`).then((r) => r.data),
  listSales: (params?: { limit?: number; shiftId?: string }) =>
    api.get('/pos/sales', { params }).then((r) => r.data.data),
  voidSale: (id: string, reason: string) =>
    api.post(`/pos/sales/${id}/void`, { reason }).then((r) => r.data),
  listTerminals: () => api.get('/pos/terminals').then((r) => r.data.data),
  createTerminal: (payload: { code?: string; name: string; branchId?: string | null }) =>
    api.post('/pos/terminals', payload).then((r) => r.data),
  updateTerminal: (id: string, payload: any) =>
    api.patch(`/pos/terminals/${id}`, payload).then((r) => r.data),
  deleteTerminal: (id: string) =>
    api.delete(`/pos/terminals/${id}`).then((r) => r.data),

  // FR-POS-01.08 / 01.09 — held carts
  holdCart: (payload: { terminalCode: string; holdReference: string; cartData: unknown; expiresInMinutes?: number }) =>
    api.post('/pos/held-carts', payload).then((r) => r.data),
  listHeldCarts: (params?: { terminalCode?: string; mine?: boolean }) =>
    api.get('/pos/held-carts', { params }).then((r) => r.data.data),
  getHeldCart: (reference: string) =>
    api.get(`/pos/held-carts/${encodeURIComponent(reference)}`).then((r) => r.data),
  releaseHeldCart: (reference: string) =>
    api.delete(`/pos/held-carts/${encodeURIComponent(reference)}`).then((r) => r.data),

  // FR-POS-01.13 — price override
  recordPriceOverride: (payload: {
    terminalCode: string;
    productId: string;
    oldPrice: number;
    newPrice: number;
    reasonCode?: string;
    reasonText?: string;
  }) => api.post('/pos/price-overrides', payload).then((r) => r.data),
  listPriceOverrides: (params?: { productId?: string; limit?: number }) =>
    api.get('/pos/price-overrides', { params }).then((r) => r.data.data),

  // FR-POS-02.06 — Z-report
  shiftSummary: (shiftId: string) =>
    api.get(`/pos/shifts/${shiftId}/summary`).then((r) => r.data),

  // FR-POS-02.07 — Mid-shift cash audit
  auditShift: (shiftId: string, countTotal: number, notes?: string) =>
    api.post(`/pos/shifts/${shiftId}/audit`, { countTotal, notes }).then((r) => r.data),

  // FR-POS-04.01 — Returns
  processReturn: (payload: {
    originalSaleId: string;
    lines: Array<{ originalSaleLineId: string; quantity: number; condition: 'resellable' | 'damaged' }>;
    refundMethod: 'original' | 'cash' | 'store_credit';
    reason: string;
  }) => api.post('/pos/returns', payload).then((r) => r.data),
  listReturns: (params?: { saleId?: string; limit?: number }) =>
    api.get('/pos/returns', { params }).then((r) => r.data.data),
  getReturn: (id: string) => api.get(`/pos/returns/${id}`).then((r) => r.data),

  // FR-POS-01.15 / 01.16 — Loyalty
  getLoyaltyStatus: (customerId: string) =>
    api.get(`/pos/loyalty/status/${customerId}`).then((r) => r.data),
  previewAccrual: (netSubtotal: number) =>
    api.get('/pos/loyalty/preview-accrual', { params: { netSubtotal } }).then((r) => r.data),
  previewRedemption: (customerId: string, grandTotal: number, points: number) =>
    api.get('/pos/loyalty/preview-redemption', {
      params: { customerId, grandTotal, points }
    }).then((r) => r.data),

  // FR-POS-01.17 — Customer-facing display
  // GET is public (no auth) so a kiosk can poll without logging in.
  // Uses tenant id in path to avoid cross-tenant leaks.
  getCustomerDisplay: (tenantId: string, terminalCode: string) =>
    api.get(`/pos/customer-display/${encodeURIComponent(tenantId)}/${encodeURIComponent(terminalCode)}`)
      .then((r) => r.data),
  pushCustomerDisplay: (terminalCode: string, state: any) =>
    api.post(`/pos/customer-display/${encodeURIComponent(terminalCode)}`, state).then((r) => r.data),

  // FR-POS-01.18 — Email receipt
  emailReceipt: (saleId: string, email: string) =>
    api.post(`/pos/receipts/${saleId}/email`, { email }).then((r) => r.data),

  // FR-POS-01.19 — SMS receipt
  smsReceipt: (saleId: string, phone: string) =>
    api.post(`/pos/receipts/${saleId}/sms`, { phone }).then((r) => r.data),

  // FR-POS-02.01 — Offline sales sync
  syncOfflineSales: (sales: any[]) =>
    api.post('/pos/sync/offline-sales', { sales }).then((r) => r.data),

  // FR-POS-03.10 / 03.11 / 03.12 — Gift cards
  issueGiftCard: (payload: {
    code?: string; pin?: string; initialValue: number;
    currency?: string; issuedToCustomerId?: string | null;
    issuedAtSaleId?: string | null; expiresAt?: string; notes?: string;
  }) => api.post('/pos/gift-cards/issue', payload).then((r) => r.data),
  checkGiftCard: (code: string, pin?: string) =>
    api.get(`/pos/gift-cards/${encodeURIComponent(code)}`, { params: { pin } }).then((r) => r.data),
  listGiftCards: (params?: { status?: string; limit?: number }) =>
    api.get('/pos/gift-cards', { params }).then((r) => r.data.data),
};

export const inventoryApi = {
  listProducts: (params?: { q?: string; page?: number; pageSize?: number; status?: string }) =>
    api.get('/inventory/products', { params }).then((r) => r.data),
  getProduct: (id: string) => api.get(`/inventory/products/${id}`).then((r) => r.data),
  createProduct: (payload: any) => api.post('/inventory/products', payload).then((r) => r.data),
  updateProduct: (id: string, payload: any) =>
    api.patch(`/inventory/products/${id}`, payload).then((r) => r.data),
  deleteProduct: (id: string) => api.delete(`/inventory/products/${id}`).then((r) => r.data),
  listCategories: () => api.get('/inventory/categories').then((r) => r.data.data),
  createCategory: (payload: { name: string; parentId?: string }) =>
    api.post('/inventory/categories', payload).then((r) => r.data),
  updateCategory: (id: string, payload: { name?: string; parentId?: string | null; status?: string }) =>
    api.patch(`/inventory/categories/${id}`, payload).then((r) => r.data),
  deleteCategory: (id: string) =>
    api.delete(`/inventory/categories/${id}`).then((r) => r.data),
  listBrands: () => api.get('/inventory/brands').then((r) => r.data.data),
  createBrand: (payload: { name: string }) =>
    api.post('/inventory/brands', payload).then((r) => r.data),
  updateBrand: (id: string, payload: { name?: string; status?: string }) =>
    api.patch(`/inventory/brands/${id}`, payload).then((r) => r.data),
  deleteBrand: (id: string) =>
    api.delete(`/inventory/brands/${id}`).then((r) => r.data),
  listWarehouses: () => api.get('/inventory/warehouses').then((r) => r.data.data),
  createWarehouse: (payload: { code?: string; name: string; type?: string; branchId?: string | null }) =>
    api.post('/inventory/warehouses', payload).then((r) => r.data),
  updateWarehouse: (id: string, payload: any) =>
    api.patch(`/inventory/warehouses/${id}`, payload).then((r) => r.data),
  deleteWarehouse: (id: string) =>
    api.delete(`/inventory/warehouses/${id}`).then((r) => r.data),
  getStock: (productId: string) =>
    api.get(`/inventory/stock/${productId}`).then((r) => r.data.data),
  getMovements: (productId: string, limit = 100) =>
    api.get(`/inventory/stock-movements/${productId}`, { params: { limit } }).then((r) => r.data.data),

  // FR-INV-02.05 / 02.06 — Adjustments
  createAdjustment: (payload: {
    warehouseId: string;
    type: 'increase' | 'decrease' | 'write_off' | 'cycle_count' | 'damage';
    reason?: string;
    lines: Array<{ productId: string; quantity: number; unitCost?: number; notes?: string }>;
  }) => api.post('/inventory/adjustments', payload).then((r) => r.data),
  listAdjustments: (params?: { status?: string; warehouseId?: string; limit?: number }) =>
    api.get('/inventory/adjustments', { params }).then((r) => r.data.data),
  getAdjustment: (id: string) =>
    api.get(`/inventory/adjustments/${id}`).then((r) => r.data),
  decideAdjustment: (id: string, approve: boolean, reason?: string) =>
    api.post(`/inventory/adjustments/${id}/approve`, { approve, reason }).then((r) => r.data),

  // FR-INV-02.07 / 02.08 — Transfers
  createTransfer: (payload: {
    fromWarehouseId: string;
    toWarehouseId: string;
    transferDate?: string;
    lines: Array<{ productId: string; quantity: number }>;
    notes?: string;
  }) => api.post('/inventory/transfers', payload).then((r) => r.data),
  listTransfers: (params?: { status?: string; limit?: number }) =>
    api.get('/inventory/transfers', { params }).then((r) => r.data.data),
  getTransfer: (id: string) =>
    api.get(`/inventory/transfers/${id}`).then((r) => r.data),
  shipTransfer: (id: string) =>
    api.post(`/inventory/transfers/${id}/ship`).then((r) => r.data),
  receiveTransfer: (id: string, lines: Array<{ lineId: string; receivedQty: number }>, notes?: string) =>
    api.post(`/inventory/transfers/${id}/receive`, { lines, notes }).then((r) => r.data),

  // FR-INV-04.01 / 04.02 — Warehouse locations
  listLocations: (warehouseId: string) =>
    api.get(`/inventory/locations/${warehouseId}`).then((r) => r.data.data),
  createLocation: (payload: {
    warehouseId: string; code: string; name: string;
    type?: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';
    parentId?: string | null;
  }) => api.post('/inventory/locations', payload).then((r) => r.data),

  // FR-INV-04.05 / 04.06 / 04.07 — Stock counts
  startCount: (payload: {
    warehouseId: string;
    countType?: 'full' | 'cycle';
    blind?: boolean;
    productIds?: string[];
    notes?: string;
  }) => api.post('/inventory/counts', payload).then((r) => r.data),
  listCounts: (params?: { status?: string; warehouseId?: string; limit?: number }) =>
    api.get('/inventory/counts', { params }).then((r) => r.data.data),
  getCount: (id: string) => api.get(`/inventory/counts/${id}`).then((r) => r.data),
  recordCountLine: (countId: string, payload: {
    productId: string; countedQty: number; locationId?: string | null;
  }) => api.post(`/inventory/counts/${countId}/lines`, payload).then((r) => r.data),
  completeCount: (countId: string, reason?: string) =>
    api.post(`/inventory/counts/${countId}/complete`, { reason }).then((r) => r.data),

  // FR-INV-02.20 — Reorder suggestions
  getReorderSuggestions: (warehouseId?: string) =>
    api.get('/inventory/reorder-suggestions', { params: { warehouseId } }).then((r) => r.data.data),

  // FR-INV-02.18 — Supplier returns (RMA)
  createRma: (payload: {
    supplierId: string;
    warehouseId: string;
    reason?: string;
    notes?: string;
    lines: Array<{
      productId: string;
      quantity: number;
      unitCost: number;
      reasonCode?: 'defective' | 'wrong_item' | 'over_shipment' | 'other';
      notes?: string;
    }>;
  }) => api.post('/inventory/returns-to-supplier', payload).then((r) => r.data),
  listRmas: (params?: { status?: string; supplierId?: string; limit?: number }) =>
    api.get('/inventory/returns-to-supplier', { params }).then((r) => r.data.data),
  getRma: (id: string) =>
    api.get(`/inventory/returns-to-supplier/${id}`).then((r) => r.data),
  shipRma: (id: string) =>
    api.post(`/inventory/returns-to-supplier/${id}/ship`).then((r) => r.data),
  completeRma: (id: string) =>
    api.post(`/inventory/returns-to-supplier/${id}/complete`).then((r) => r.data),

  // FR-INV-01.02 — Variants
  listVariants: (productId: string) =>
    api.get(`/inventory/products/${productId}/variants`).then((r) => r.data.data),
  createVariant: (productId: string, payload: {
    sku: string; barcode?: string | null;
    attributes?: Record<string, string>;
    priceAdjustment?: number;
    status?: 'active' | 'discontinued';
  }) => api.post(`/inventory/products/${productId}/variants`, payload).then((r) => r.data),
  updateVariant: (productId: string, variantId: string, payload: any) =>
    api.patch(`/inventory/products/${productId}/variants/${variantId}`, payload).then((r) => r.data),
  deleteVariant: (productId: string, variantId: string) =>
    api.delete(`/inventory/products/${productId}/variants/${variantId}`).then((r) => r.data),
  updateLocation: (id: string, payload: any) =>
    api.patch(`/inventory/locations/${id}`, payload).then((r) => r.data),
  deleteLocation: (id: string) =>
    api.delete(`/inventory/locations/${id}`).then((r) => r.data),

  // FR-INV-02.09 / 02.10 — Serial numbers
  recordSerials: (payload: {
    productId: string;
    variantId?: string | null;
    warehouseId: string;
    serials: string[];
    unitCost?: number;
    referenceType?: string;
    referenceId?: string | null;
    notes?: string;
  }) => api.post('/inventory/serial-numbers', payload).then((r) => r.data),
  listSerials: (params?: { productId?: string; status?: string; warehouseId?: string; limit?: number }) =>
    api.get('/inventory/serial-numbers', { params }).then((r) => r.data.data),
  getSerialHistory: (serial: string) =>
    api.get(`/inventory/serial-numbers/${encodeURIComponent(serial)}`).then((r) => r.data),

  // FR-INV-02.12 / 02.15 — Batches
  listBatches: (productId: string, activeOnly = true) =>
    api.get(`/inventory/batches/${productId}`, { params: { activeOnly } }).then((r) => r.data.data),
  getExpiringBatches: (params?: { days?: number; warehouseId?: string }) =>
    api.get('/inventory/batches/expiring-soon', { params }).then((r) => r.data.data),

  // FR-INV-01.10 — Barcode labels (returns Blob)
  generateLabelsPdf: (format: 'avery_5160' | 'thermal_2x1', items: Array<{ productId: string; quantity: number }>) =>
    api.post('/inventory/barcodes/generate', { format, items }, { responseType: 'blob' }).then((r) => r.data),

  // FR-INV-01.11 — CSV import
  importProductsCsv: (file: File, abortOnError = false) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/inventory/bulk-import', form, {
      params: { abort_on_error: abortOnError },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  downloadImportTemplate: () =>
    api.get('/inventory/bulk-import/template', { responseType: 'blob' }).then((r) => r.data),

  // FR-INV-01.12 — CSV export
  exportProductsCsv: () =>
    api.get('/inventory/bulk-export', { responseType: 'blob' }).then((r) => r.data),
};

export const accountingApi = {
  listAccounts: (type?: string) =>
    api.get('/accounting/accounts', { params: type ? { type } : {} }).then((r) => r.data.data),
  createAccount: (payload: {
    code: string; name: string; account_type: string;
    normal_balance: 'debit' | 'credit'; currency?: string;
    is_bank_account?: boolean; parent_id?: string;
  }) => api.post('/accounting/accounts', payload).then((r) => r.data),
  updateAccount: (id: string, payload: { name?: string; status?: string }) =>
    api.patch(`/accounting/accounts/${id}`, payload).then((r) => r.data),
  getAccountBalance: (id: string) =>
    api.get(`/accounting/accounts/${id}/balance`).then((r) => r.data),
  listJournalEntries: (params?: { status?: string; limit?: number }) =>
    api.get('/accounting/journal-entries', { params }).then((r) => r.data.data),
  getJournalEntry: (id: string) =>
    api.get(`/accounting/journal-entries/${id}`).then((r) => r.data),
  createJournalEntry: (payload: any) =>
    api.post('/accounting/journal-entries', payload).then((r) => r.data),
  postJournalEntry: (id: string) =>
    api.post(`/accounting/journal-entries/${id}/post`).then((r) => r.data),

  // FR-ACC-06 — Financial reports
  trialBalance: (params?: { asOfDate?: string; includeZero?: boolean }) =>
    api.get('/accounting/reports/trial-balance', { params }).then((r) => r.data),
  balanceSheet: (asOfDate?: string) =>
    api.get('/accounting/reports/balance-sheet', { params: asOfDate ? { asOfDate } : {} }).then((r) => r.data),
  incomeStatement: (params?: { fromDate?: string; toDate?: string }) =>
    api.get('/accounting/reports/income-statement', { params }).then((r) => r.data),
  cashFlow: (params?: { fromDate?: string; toDate?: string }) =>
    api.get('/accounting/reports/cash-flow', { params }).then((r) => r.data),
  generalLedger: (params: { accountId: string; fromDate?: string; toDate?: string }) =>
    api.get('/accounting/reports/general-ledger', { params }).then((r) => r.data),

  // FR-ACC-03 — AR workflow
  listInvoices: (params?: { status?: string; customerId?: string; limit?: number }) =>
    api.get('/accounting/ar/invoices', { params }).then((r) => r.data.data),
  getInvoice: (id: string) => api.get(`/accounting/ar/invoices/${id}`).then((r) => r.data),
  deleteInvoice: (id: string) =>
    api.delete(`/accounting/ar/invoices/${id}`).then((r) => r.data),
  deleteJournalEntry: (id: string) =>
    api.delete(`/accounting/journal-entries/${id}`).then((r) => r.data),
  reverseArPayment: (id: string) =>
    api.post(`/accounting/ar/payments/${id}/reverse`, {}).then((r) => r.data),
  reverseApPayment: (id: string) =>
    api.post(`/accounting/ap/payments/${id}/reverse`, {}).then((r) => r.data),
  createInvoice: (payload: {
    customerId: string;
    invoiceDate?: string;
    dueDate: string;
    paymentTerms?: string;
    currency?: string;
    notes?: string;
    autoPost?: boolean;
    lines: Array<{
      productId?: string;
      description: string;
      quantity?: number;
      unitPrice: number;
      discountAmount?: number;
      taxRate?: number;
    }>;
  }) => api.post('/accounting/ar/invoices', payload).then((r) => r.data),
  emailInvoice: (id: string, payload?: { recipientEmail?: string; subject?: string }) =>
    api.post(`/accounting/ar/invoices/${id}/send`, payload ?? {}).then((r) => r.data),
  recordCustomerPayment: (payload: {
    customerId: string;
    paymentDate?: string;
    method: string;
    amount: number;
    currency?: string;
    reference?: string;
    bankAccountCode?: string;
    gateway?: string;
    gatewayTxnId?: string;
    cardLast4?: string;
    cardBrand?: string;
    voucherType?: 'CRV' | 'BRV';
    notes?: string;
    applications: Array<{ invoiceId: string; amount: number }>;
  }) => api.post('/accounting/ar/payments', payload).then((r) => r.data),
  listCustomerPayments: (params?: { customerId?: string; limit?: number }) =>
    api.get('/accounting/ar/payments', { params }).then((r) => r.data.data),
  arAging: (asOfDate?: string) =>
    api.get('/accounting/ar/aging', { params: asOfDate ? { asOfDate } : {} }).then((r) => r.data),
  runDunning: (force: boolean = false) =>
    api.post('/accounting/ar/dunning', { force }).then((r) => r.data),
  listCreditNotes: (params?: { customerId?: string; status?: string; limit?: number }) =>
    api.get('/accounting/ar/credit-notes', { params }).then((r) => r.data.data),
  createCreditNote: (payload: {
    customerId: string;
    sourceInvoiceId?: string;
    amount: number;
    reason: string;
    applyToInvoiceId?: string;
  }) => api.post('/accounting/ar/credit-notes', payload).then((r) => r.data),
  applyCreditNote: (creditNoteId: string, invoiceId: string, amount: number) =>
    api.post(`/accounting/ar/credit-notes/${creditNoteId}/apply`, { invoiceId, amount }).then((r) => r.data),
  writeOffInvoice: (invoiceId: string, reason: string) =>
    api.post('/accounting/ar/write-off', { invoiceId, reason }).then((r) => r.data),

  // FR-ACC-04 — AP workflow
  listVendorInvoices: (params?: { status?: string; supplierId?: string; matchStatus?: string; limit?: number }) =>
    api.get('/accounting/ap/invoices', { params }).then((r) => r.data.data),
  getVendorInvoice: (id: string) => api.get(`/accounting/ap/invoices/${id}`).then((r) => r.data),
  createVendorInvoice: (payload: {
    supplierId: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    currency?: string;
    poId?: string | null;
    grnId?: string | null;
    notes?: string;
    mode: 'three_way_match' | 'expense';
    expenseAccountCode?: string;
    lines: Array<{
      poLineId?: string | null;
      productId?: string | null;
      description: string;
      quantity: number;
      unitPrice: number;
      taxRate?: number;
    }>;
  }) => api.post('/accounting/ap/invoices', payload).then((r) => r.data),
  decideVendorInvoice: (id: string, approve: boolean, reason?: string) =>
    api.post(`/accounting/ap/invoices/${id}/approve`, { approve, reason }).then((r) => r.data),
  resolveInvoiceVariance: (id: string, notes: string) =>
    api.post(`/accounting/ap/invoices/${id}/resolve-variance`, { notes }).then((r) => r.data),
  listVendorPayments: (params?: { supplierId?: string; limit?: number }) =>
    api.get('/accounting/ap/payments', { params }).then((r) => r.data.data),
  recordVendorPayment: (payload: {
    supplierId: string;
    paymentDate?: string;
    method: string;
    amount: number;
    currency?: string;
    reference?: string;
    bankAccountCode?: string;
    gateway?: string;
    gatewayTxnId?: string;
    cardLast4?: string;
    cardBrand?: string;
    voucherType?: 'CPV' | 'BPV';
    notes?: string;
    applications: Array<{ invoiceId: string; amount: number }>;
  }) => api.post('/accounting/ap/payments', payload).then((r) => r.data),
  apAging: (asOfDate?: string) =>
    api.get('/accounting/ap/aging', { params: asOfDate ? { asOfDate } : {} }).then((r) => r.data),

  // Payment runs
  listPaymentRuns: (params?: { status?: string; limit?: number }) =>
    api.get('/accounting/ap/payment-runs', { params }).then((r) => r.data.data),
  getPaymentRun: (id: string) => api.get(`/accounting/ap/payment-runs/${id}`).then((r) => r.data),
  createPaymentRun: (payload: {
    runDate?: string;
    method: 'ach' | 'wire' | 'check';
    bankAccountCode?: string;
    notes?: string;
    dueBy?: string;
    supplierIds?: string[];
  }) => api.post('/accounting/ap/payment-runs', payload).then((r) => r.data),
  approvePaymentRun: (id: string) =>
    api.post(`/accounting/ap/payment-runs/${id}/approve`).then((r) => r.data),
  executePaymentRun: (id: string) =>
    api.post(`/accounting/ap/payment-runs/${id}/execute`).then((r) => r.data),
  cancelPaymentRun: (id: string, reason: string) =>
    api.post(`/accounting/ap/payment-runs/${id}/cancel`, { reason }).then((r) => r.data),
  removeRunItem: (runId: string, itemId: string) =>
    api.delete(`/accounting/ap/payment-runs/${runId}/items/${itemId}`).then((r) => r.data),

  // FR-ACC-02.05 — Reverse JE
  reverseJournalEntry: (id: string, payload: { reversalDate?: string; reason: string }) =>
    api.post(`/accounting/journal-entries/${id}/reverse`, payload).then((r) => r.data),

  // FR-ACC-07 — Period close
  listPeriods: (fiscalYear?: number) =>
    api.get('/accounting/periods', { params: fiscalYear ? { fiscalYear } : {} }).then((r) => r.data.data),
  closePeriod: (period: string, notes?: string) =>
    api.post(`/accounting/periods/${period}/close`, { notes }).then((r) => r.data),
  reopenPeriod: (period: string, reason: string) =>
    api.post(`/accounting/periods/${period}/reopen`, { reason }).then((r) => r.data),
  closeYearEnd: (payload: { fiscalYear: number; retainedEarningsCode?: string; notes?: string }) =>
    api.post('/accounting/periods/year-end', payload).then((r) => r.data),

  // FR-ACC-10 — Recurring JEs
  listRecurringTemplates: (status?: string) =>
    api.get('/accounting/recurring', { params: status ? { status } : {} }).then((r) => r.data.data),
  getRecurringTemplate: (id: string) =>
    api.get(`/accounting/recurring/${id}`).then((r) => r.data),
  createRecurringTemplate: (payload: {
    name: string;
    description?: string;
    schedule: 'monthly' | 'quarterly' | 'annually';
    nextRunDate: string;
    endDate?: string;
    template: {
      description: string;
      lines: Array<{ accountCode: string; debit?: number; credit?: number; description?: string }>;
    };
  }) => api.post('/accounting/recurring', payload).then((r) => r.data),
  pauseRecurringTemplate: (id: string) =>
    api.post(`/accounting/recurring/${id}/pause`).then((r) => r.data),
  resumeRecurringTemplate: (id: string) =>
    api.post(`/accounting/recurring/${id}/resume`).then((r) => r.data),
  runRecurringTemplate: (id: string, overrideDate?: string) =>
    api.post(`/accounting/recurring/${id}/run`, overrideDate ? { overrideDate } : {}).then((r) => r.data),

  // FR-ACC-05 — Bank reconciliation
  listBankStatements: (params?: { accountId?: string; status?: string; limit?: number }) =>
    api.get('/accounting/bank/statements', { params }).then((r) => r.data.data),
  getBankStatement: (id: string) =>
    api.get(`/accounting/bank/statements/${id}`).then((r) => r.data),
  importBankStatement: (payload: {
    accountId: string;
    statementDate: string;
    openingBalance: number;
    closingBalance: number;
    fileName?: string;
    format?: 'csv' | 'ofx' | 'mt940' | 'api';
    notes?: string;
    lines: Array<{
      txnDate: string;
      description?: string;
      reference?: string;
      amount: number;
    }>;
  }) => api.post('/accounting/bank/statements/import', payload).then((r) => r.data),
  autoMatchStatement: (statementId: string, opts?: { dateToleranceDays?: number; amountTolerance?: number }) =>
    api.post(`/accounting/bank/reconcile/${statementId}`, opts ?? {}).then((r) => r.data),
  manualMatchLine: (lineId: string, journalLineId: string, notes?: string) =>
    api.post(`/accounting/bank/reconcile/line/${lineId}/match`, { journalLineId, notes }).then((r) => r.data),
  unmatchLine: (lineId: string) =>
    api.post(`/accounting/bank/reconcile/line/${lineId}/unmatch`).then((r) => r.data),
  ignoreLine: (lineId: string, reason: string) =>
    api.post(`/accounting/bank/reconcile/line/${lineId}/ignore`, { reason }).then((r) => r.data),
  getMatchCandidates: (lineId: string) =>
    api.get(`/accounting/bank/reconcile/line/${lineId}/candidates`).then((r) => r.data),
  closeBankStatement: (id: string) =>
    api.post(`/accounting/bank/statements/${id}/close`).then((r) => r.data),

  // FR-ACC-08 — FX
  listFxRates: (params?: { currency?: string; limit?: number }) =>
    api.get('/accounting/fx/rates', { params }).then((r) => r.data.data),
  recordFxRate: (payload: {
    fromCurrency: string; toCurrency: string; rate: number;
    effectiveDate: string; source?: string;
  }) => api.post('/accounting/fx/rates', payload).then((r) => r.data),
  runFxRevaluation: (payload: {
    asOfDate: string; baseCurrency?: string;
    currencies?: string[]; notes?: string;
  }) => api.post('/accounting/fx/revalue', payload).then((r) => r.data),
  listFxRevaluations: (limit?: number) =>
    api.get('/accounting/fx/revaluations', { params: limit ? { limit } : {} }).then((r) => r.data.data),

  // FR-ACC-09 — Tax
  listTaxClasses: () => api.get('/accounting/tax/classes').then((r) => r.data.data),
  createTaxClass: (payload: {
    code: string; name: string; rate_percent: number;
    tax_type?: string; jurisdiction?: string; is_compound?: boolean; inclusive?: boolean;
  }) => api.post('/accounting/tax/classes', payload).then((r) => r.data),
  updateTaxClass: (id: string, payload: any) =>
    api.patch(`/accounting/tax/classes/${id}`, payload).then((r) => r.data),
  deleteTaxClass: (id: string) =>
    api.delete(`/accounting/tax/classes/${id}`).then((r) => r.data),
  getTaxLiability: (params: { fromDate: string; toDate: string; jurisdiction?: string; taxType?: string }) =>
    api.get('/accounting/tax/liability', { params }).then((r) => r.data),
  listTaxReturns: (params?: { status?: string; taxType?: string; limit?: number }) =>
    api.get('/accounting/tax/returns', { params }).then((r) => r.data.data),
  getTaxReturn: (id: string) => api.get(`/accounting/tax/returns/${id}`).then((r) => r.data),
  createTaxReturn: (payload: {
    taxType: 'sales' | 'vat' | 'gst' | 'excise' | 'other';
    jurisdiction?: string;
    periodFrom: string;
    periodTo: string;
    notes?: string;
  }) => api.post('/accounting/tax/returns', payload).then((r) => r.data),
  fileTaxReturn: (id: string, fileReference?: string) =>
    api.post(`/accounting/tax/returns/${id}/file`, fileReference ? { fileReference } : {}).then((r) => r.data),
  voidTaxReturn: (id: string, reason: string) =>
    api.post(`/accounting/tax/returns/${id}/void`, { reason }).then((r) => r.data),
};

export const salesApi = {
  listCustomers: (params?: { q?: string; limit?: number }) =>
    api.get('/sales/customers', { params }).then((r) => r.data.data),
  getCustomer: (id: string) => api.get(`/sales/customers/${id}`).then((r) => r.data),
  createCustomer: (payload: any) => api.post('/sales/customers', payload).then((r) => r.data),
  updateCustomer: (id: string, payload: any) =>
    api.patch(`/sales/customers/${id}`, payload).then((r) => r.data),
  deleteCustomer: (id: string) =>
    api.delete(`/sales/customers/${id}`).then((r) => r.data),

  // FR-SALES-01 — Orders
  listOrders: (params?: { status?: string; customerId?: string; limit?: number }) =>
    api.get('/sales/orders', { params }).then((r) => r.data.data),
  getOrder: (id: string) => api.get(`/sales/orders/${id}`).then((r) => r.data),
  createOrder: (payload: {
    customerId: string;
    orderDate?: string;
    requiredDate?: string;
    warehouseId?: string;
    salesRepId?: string;
    paymentTerms?: string;
    shippingAddress?: Record<string, unknown>;
    billingAddress?: Record<string, unknown>;
    notes?: string;
    shippingTotal?: number;
    lines: Array<{
      productId: string;
      variantId?: string | null;
      description?: string;
      quantity: number;
      unitPrice: number;
      discountPercent?: number;
      discountAmount?: number;
      taxRate?: number;
    }>;
  }) => api.post('/sales/orders', payload).then((r) => r.data),
  updateOrder: (id: string, payload: any) =>
    api.patch(`/sales/orders/${id}`, payload).then((r) => r.data),
  confirmOrder: (id: string) =>
    api.post(`/sales/orders/${id}/confirm`).then((r) => r.data),
  cancelOrder: (id: string, reason: string) =>
    api.post(`/sales/orders/${id}/cancel`, { reason }).then((r) => r.data),
  pickOrder: (id: string, picks: Array<{ lineId: string; pickedQty: number }>) =>
    api.post(`/sales/orders/${id}/pick`, { picks }).then((r) => r.data),
  packOrder: (id: string, packs: Array<{ lineId: string; packedQty: number }>) =>
    api.post(`/sales/orders/${id}/pack`, { packs }).then((r) => r.data),
  shipOrder: (id: string, payload: {
    trackingCarrier?: string;
    trackingNumber?: string;
    shipAll?: boolean;
    shipments?: Array<{ lineId: string; shippedQty: number }>;
  }) => api.post(`/sales/orders/${id}/ship`, payload).then((r) => r.data),
  invoiceOrder: (id: string, payload?: { dueDate?: string; autoPost?: boolean }) =>
    api.post(`/sales/orders/${id}/invoice`, payload ?? {}).then((r) => r.data),

  // FR-SALES-02 — Quotations
  listQuotes: (params?: { status?: string; customerId?: string; limit?: number }) =>
    api.get('/sales/quotes', { params }).then((r) => r.data.data),
  getQuote: (id: string) => api.get(`/sales/quotes/${id}`).then((r) => r.data),
  createQuote: (payload: {
    customerId: string;
    quoteDate?: string;
    validUntil?: string;
    salesRepId?: string;
    notes?: string;
    shippingTotal?: number;
    lines: Array<{
      productId: string;
      variantId?: string | null;
      description?: string;
      quantity: number;
      unitPrice: number;
      discountPercent?: number;
      discountAmount?: number;
      taxRate?: number;
    }>;
  }) => api.post('/sales/quotes', payload).then((r) => r.data),
  deleteQuote: (id: string) =>
    api.delete(`/sales/quotes/${id}`).then((r) => r.data),
  updateQuote: (id: string, payload: any) =>
    api.patch(`/sales/quotes/${id}`, payload).then((r) => r.data),
  sendQuote: (id: string, payload?: { recipientEmail?: string; subject?: string }) =>
    api.post(`/sales/quotes/${id}/send`, payload ?? {}).then((r) => r.data),
  decideQuote: (id: string, accept: boolean, reason?: string) =>
    api.post(`/sales/quotes/${id}/decide`, { accept, reason }).then((r) => r.data),
  convertQuote: (id: string, payload?: {
    warehouseId?: string; requiredDate?: string; paymentTerms?: string;
  }) => api.post(`/sales/quotes/${id}/convert`, payload ?? {}).then((r) => r.data),
  expireQuotes: () => api.post('/sales/quotes/expire-all').then((r) => r.data),

  // FR-SALES-03.04-07 — Customer intelligence
  getCustomerStatement: (id: string, params?: { fromDate?: string; toDate?: string }) =>
    api.get(`/sales/customers/${id}/ledger`, { params }).then((r) => r.data),
  getCustomerPurchaseHistory: (id: string, limit?: number) =>
    api.get(`/sales/customers/${id}/purchase-history`, { params: limit ? { limit } : {} }).then((r) => r.data),
  listCustomerCommunications: (id: string, limit?: number) =>
    api.get(`/sales/customers/${id}/communications`, { params: limit ? { limit } : {} }).then((r) => r.data.data),
  logCustomerCommunication: (id: string, payload: {
    channel: 'email' | 'sms' | 'call' | 'whatsapp' | 'note';
    direction?: 'in' | 'out';
    subject?: string;
    body?: string;
    relatedType?: string;
    relatedId?: string;
  }) => api.post(`/sales/customers/${id}/communications`, payload).then((r) => r.data),
  getSegmentsSummary: () => api.get('/sales/customers/segments').then((r) => r.data),
  recalculateSegments: (payload: { lookbackDays?: number; persistSegments?: boolean }) =>
    api.post('/sales/customers/segments/recalc', payload).then((r) => r.data),

  // FR-SALES-04 — Leads
  listLeads: (params?: { stage?: string; assignedTo?: string; q?: string; limit?: number }) =>
    api.get('/sales/leads', { params }).then((r) => r.data.data),
  getLead: (id: string) => api.get(`/sales/leads/${id}`).then((r) => r.data),
  createLead: (payload: {
    name: string;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
    source?: string;
    stage?: 'prospect' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost';
    forecastValue?: number;
    expectedCloseDate?: string;
    probability?: number;
    assignedTo?: string | null;
    notes?: string;
  }) => api.post('/sales/leads', payload).then((r) => r.data),
  updateLead: (id: string, payload: any) =>
    api.patch(`/sales/leads/${id}`, payload).then((r) => r.data),
  advanceLeadStage: (id: string, payload: {
    toStage: 'prospect' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost';
    notes?: string;
    probability?: number;
    lostReason?: string;
  }) => api.patch(`/sales/leads/${id}/stage`, payload).then((r) => r.data),
  convertLead: (id: string, payload: {
    customerCode: string;
    customerType?: 'individual' | 'business';
    paymentTerms?: string;
    creditLimit?: number;
    createOrder?: boolean;
  }) => api.post(`/sales/leads/${id}/convert`, payload).then((r) => r.data),
  getPipelineForecast: (params?: { fromDate?: string; toDate?: string; assignedTo?: string }) =>
    api.get('/sales/leads/forecast', { params }).then((r) => r.data),

  // FR-SALES-05 — Promotions + coupons
  listPromotions: (params?: { status?: string; type?: string; activeOnly?: boolean; limit?: number }) =>
    api.get('/sales/promotions', { params }).then((r) => r.data.data),
  getPromotion: (id: string) => api.get(`/sales/promotions/${id}`).then((r) => r.data),
  createPromotion: (payload: {
    code?: string; name: string; description?: string;
    type: 'percentage' | 'fixed_amount' | 'bogo' | 'tiered';
    value: number; minPurchase?: number;
    startsAt: string; endsAt: string;
    customerSegments?: string[];
    stackable?: boolean;
    usageLimit?: number;
    generateCoupons?: number;
    couponPrefix?: string;
  }) => api.post('/sales/promotions', payload).then((r) => r.data),
  updatePromotion: (id: string, payload: any) =>
    api.patch(`/sales/promotions/${id}`, payload).then((r) => r.data),
  listCoupons: (params?: { promotionId?: string; status?: string; customerId?: string; limit?: number }) =>
    api.get('/sales/coupons', { params }).then((r) => r.data.data),
  issueCoupons: (payload: {
    promotionId: string; count: number; prefix?: string;
    usageLimit?: number; expiresAt?: string; customerId?: string;
  }) => api.post('/sales/coupons/issue-batch', payload).then((r) => r.data),
  validateCoupon: (payload: { code: string; subtotal?: number; customerId?: string }) =>
    api.post('/sales/coupons/validate', payload).then((r) => r.data),

  // FR-SALES-06 — Commissions
  listCommissionRules: (params?: { userId?: string; status?: string }) =>
    api.get('/sales/commissions/rules', { params }).then((r) => r.data.data),
  createCommissionRule: (payload: {
    userId?: string | null; name: string; ratePercent: number;
    minInvoiceAmount?: number; maxInvoiceAmount?: number;
    productCategoryId?: string;
    effectiveFrom?: string; effectiveTo?: string;
    priority?: number;
  }) => api.post('/sales/commissions/rules', payload).then((r) => r.data),
  updateCommissionRule: (id: string, payload: any) =>
    api.patch(`/sales/commissions/rules/${id}`, payload).then((r) => r.data),
  calculateCommissionStatements: (payload: {
    periodFrom: string; periodTo: string;
    userIds?: string[]; replaceExisting?: boolean;
  }) => api.post('/sales/commissions/calculate', payload).then((r) => r.data),
  listCommissionStatements: (params?: { userId?: string; status?: string; limit?: number }) =>
    api.get('/sales/commissions/statements', { params }).then((r) => r.data.data),
  getCommissionStatement: (id: string) =>
    api.get(`/sales/commissions/statements/${id}`).then((r) => r.data),
  approveCommissionStatement: (id: string) =>
    api.post(`/sales/commissions/statements/${id}/approve`).then((r) => r.data),
  payCommissionStatement: (id: string) =>
    api.post(`/sales/commissions/statements/${id}/pay`).then((r) => r.data),
  voidCommissionStatement: (id: string, reason?: string) =>
    api.post(`/sales/commissions/statements/${id}/void`, { reason }).then((r) => r.data),
};

export const purchaseApi = {
  listSuppliers: (params?: { q?: string }) =>
    api.get('/purchase/suppliers', { params }).then((r) => r.data.data),
  getSupplier: (id: string) => api.get(`/purchase/suppliers/${id}`).then((r) => r.data),
  createSupplier: (payload: any) => api.post('/purchase/suppliers', payload).then((r) => r.data),
  updateSupplier: (id: string, payload: any) =>
    api.patch(`/purchase/suppliers/${id}`, payload).then((r) => r.data),
  deleteSupplier: (id: string) =>
    api.delete(`/purchase/suppliers/${id}`).then((r) => r.data),
  deletePo: (id: string) =>
    api.delete(`/purchase/orders/${id}`).then((r) => r.data),

  // FR-PUR-01.01–05 — Purchase orders
  listOrders: (params?: { status?: string; supplierId?: string; limit?: number }) =>
    api.get('/purchase/orders', { params }).then((r) => r.data.data),
  getOrder: (id: string) => api.get(`/purchase/orders/${id}`).then((r) => r.data),
  createOrder: (payload: {
    supplierId: string;
    warehouseId: string;
    orderDate?: string;
    requiredDate?: string;
    notes?: string;
    currency?: string;
    lines: Array<{
      productId: string;
      variantId?: string | null;
      description: string;
      quantity: number;
      unitPrice: number;
      taxRate?: number;
    }>;
  }) => api.post('/purchase/orders', payload).then((r) => r.data),
  updateOrder: (id: string, payload: any) =>
    api.patch(`/purchase/orders/${id}`, payload).then((r) => r.data),
  submitOrder: (id: string) =>
    api.post(`/purchase/orders/${id}/submit`).then((r) => r.data),
  decideOrder: (id: string, approve: boolean, reason?: string) =>
    api.post(`/purchase/orders/${id}/approve`, { approve, reason }).then((r) => r.data),
  cancelOrder: (id: string, reason: string) =>
    api.post(`/purchase/orders/${id}/cancel`, { reason }).then((r) => r.data),
  getOpenPoLines: (poId: string) =>
    api.get(`/purchase/orders/${poId}/open-lines`).then((r) => r.data.data),

  // FR-PUR-03 — GRN
  listGrns: (params?: { status?: string; poId?: string; limit?: number }) =>
    api.get('/purchase/grns', { params }).then((r) => r.data.data),
  getGrn: (id: string) => api.get(`/purchase/grns/${id}`).then((r) => r.data),
  createGrn: (payload: {
    poId: string;
    warehouseId: string;
    receiptDate?: string;
    supplierDeliveryNote?: string;
    notes?: string;
    lines: Array<{
      poLineId: string;
      productId: string;
      quantity: number;
      unitCost: number;
      batchNumber?: string | null;
      expiryDate?: string | null;
      locationId?: string | null;
      serialNumbers?: string[];
      rejectedQty?: number;
      rejectionReason?: string | null;
    }>;
  }) => api.post('/purchase/grns', payload).then((r) => r.data),
  confirmGrn: (id: string) => api.post(`/purchase/grns/${id}/confirm`).then((r) => r.data),
  rejectGrn: (id: string, reason: string) =>
    api.post(`/purchase/grns/${id}/reject`, { reason }).then((r) => r.data),
  rejectGrnLine: (id: string, lineId: string, rejectedQty: number, reason: string) =>
    api.post(`/purchase/grns/${id}/reject-line`, { lineId, rejectedQty, reason }).then((r) => r.data),

  // FR-PUR-04 — RTV
  listRtvs: (params?: { status?: string; limit?: number }) =>
    api.get('/purchase/returns', { params }).then((r) => r.data.data),
  getRtv: (id: string) => api.get(`/purchase/returns/${id}`).then((r) => r.data),
  createRtv: (payload: any) => api.post('/purchase/returns', payload).then((r) => r.data),
  decideRtv: (id: string, approve: boolean, reason?: string) =>
    api.post(`/purchase/returns/${id}/approve`, { approve, reason }).then((r) => r.data),
  shipRtv: (id: string) => api.post(`/purchase/returns/${id}/ship`).then((r) => r.data),
  completeRtv: (id: string) => api.post(`/purchase/returns/${id}/complete`).then((r) => r.data),

  // FR-PUR-01.04 — Email PO (stub)
  emailPo: (id: string, payload?: { recipientEmail?: string; subject?: string; body?: string }) =>
    api.post(`/purchase/orders/${id}/send`, payload ?? {}).then((r) => r.data),
  getPoEmailHistory: (id: string) =>
    api.get(`/purchase/orders/${id}/email-history`).then((r) => r.data.data),

  // FR-PUR-05 — Requisitions
  listRequisitions: (params?: { status?: string; requesterId?: string; limit?: number }) =>
    api.get('/purchase/requisitions', { params }).then((r) => r.data.data),
  getRequisition: (id: string) => api.get(`/purchase/requisitions/${id}`).then((r) => r.data),
  createRequisition: (payload: {
    department?: string;
    warehouseId?: string | null;
    neededBy?: string;
    justification?: string;
    notes?: string;
    lines: Array<{
      productId?: string | null;
      description: string;
      quantity: number;
      estimatedUnitPrice?: number;
      preferredSupplierId?: string | null;
      notes?: string;
    }>;
  }) => api.post('/purchase/requisitions', payload).then((r) => r.data),
  submitRequisition: (id: string) =>
    api.post(`/purchase/requisitions/${id}/submit`).then((r) => r.data),
  decideRequisition: (id: string, approve: boolean, reason?: string) =>
    api.post(`/purchase/requisitions/${id}/approve`, { approve, reason }).then((r) => r.data),
  convertRequisition: (id: string, payload: any) =>
    api.post(`/purchase/requisitions/${id}/convert`, payload).then((r) => r.data),
  cancelRequisition: (id: string, reason: string) =>
    api.post(`/purchase/requisitions/${id}/cancel`, { reason }).then((r) => r.data),

  // FR-PUR-06 — RFQs
  listRfqs: (params?: { status?: string; limit?: number }) =>
    api.get('/purchase/rfqs', { params }).then((r) => r.data.data),
  getRfq: (id: string) => api.get(`/purchase/rfqs/${id}`).then((r) => r.data),
  createRfq: (payload: {
    title: string;
    description?: string;
    responseDeadline?: string;
    sourceRequisitionId?: string | null;
    supplierIds: string[];
    lines: Array<{
      productId?: string | null;
      description: string;
      quantity: number;
      notes?: string;
    }>;
    notes?: string;
  }) => api.post('/purchase/rfqs', payload).then((r) => r.data),
  sendRfq: (id: string) => api.post(`/purchase/rfqs/${id}/send`).then((r) => r.data),
  recordRfqQuote: (id: string, payload: any) =>
    api.post(`/purchase/rfqs/${id}/quotes`, payload).then((r) => r.data),
  compareRfq: (id: string) => api.get(`/purchase/rfqs/${id}/compare`).then((r) => r.data),
  awardRfq: (id: string, payload: any) =>
    api.post(`/purchase/rfqs/${id}/award`, payload).then((r) => r.data),

  // FR-PUR-02.05 — Scorecard
  getSupplierScorecard: (id: string, params?: { from?: string; to?: string }) =>
    api.get(`/purchase/suppliers/${id}/performance`, { params }).then((r) => r.data),

  // FR-PUR-02.06 — Onboarding
  listPendingOnboarding: () =>
    api.get('/purchase/suppliers/pending-onboarding').then((r) => r.data.data),
  advanceOnboarding: (id: string, payload: {
    toStage: 'registration' | 'docs_submitted' | 'verified' | 'approved' | 'rejected';
    notes?: string;
    rejectionReason?: string;
    documents?: Record<string, string>;
  }) => api.post(`/purchase/suppliers/${id}/onboard`, payload).then((r) => r.data),
};

export const hrmApi = {
  listEmployees: () => api.get('/hrm/employees').then((r) => r.data.data),
  getEmployee: (id: string) => api.get(`/hrm/employees/${id}`).then((r) => r.data),
  createEmployee: (payload: any) => api.post('/hrm/employees', payload).then((r) => r.data),
  updateEmployee: (id: string, payload: { full_name?: string; designation?: string; base_salary?: number; pay_frequency?: string; status?: string }) =>
    api.patch(`/hrm/employees/${id}`, payload).then((r) => r.data),
  deleteEmployee: (id: string) =>
    api.delete(`/hrm/employees/${id}`).then((r) => r.data),
  listDepartments: () => api.get('/hrm/departments').then((r) => r.data.data),
  createDepartment: (payload: { code?: string; name: string; parentCode?: string }) =>
    api.post('/hrm/departments', payload).then((r) => r.data),

  // FR-HR-02 — Attendance
  getTodayAttendance: () => api.get('/hrm/attendance/today').then((r) => r.data),
  clockIn: (payload?: {
    employeeId?: string;
    method?: 'biometric' | 'gps' | 'web' | 'manual';
    location?: { lat: number; lng: number };
    sourceDevice?: string;
    notes?: string;
  }) => api.post('/hrm/attendance/clock-in', payload ?? {}).then((r) => r.data),
  clockOut: (payload?: {
    employeeId?: string;
    breakMinutes?: number;
    notes?: string;
  }) => api.post('/hrm/attendance/clock-out', payload ?? {}).then((r) => r.data),
  listAttendance: (employeeId: string, params?: { fromDate?: string; toDate?: string; limit?: number }) =>
    api.get(`/hrm/attendance/${employeeId}`, { params }).then((r) => r.data.data),
  regularizeAttendance: (payload: {
    employeeId: string;
    workDate: string;
    clockIn?: string;
    clockOut?: string;
    breakMinutes?: number;
    status?: 'present' | 'late' | 'half_day' | 'on_leave' | 'absent';
    reason: string;
  }) => api.post('/hrm/attendance/regularize', payload).then((r) => r.data),
  getTimesheet: (params: { fromDate: string; toDate: string; employeeId?: string; departmentId?: string }) =>
    api.get('/hrm/attendance/reports/timesheet', { params }).then((r) => r.data),

  // Attendance machines / biometric devices
  listAttendanceDevices: () => api.get('/hrm/attendance/devices').then((r) => r.data.data),
  createAttendanceDevice: (payload: {
    deviceCode: string; name: string; location?: string; branchId?: string;
    model?: string; pushProtocol?: string; notes?: string;
  }) => api.post('/hrm/attendance/devices', payload).then((r) => r.data),
  updateAttendanceDevice: (id: string, payload: {
    name?: string; location?: string; branchId?: string;
    model?: string; status?: string; notes?: string;
  }) => api.patch(`/hrm/attendance/devices/${id}`, payload).then((r) => r.data),
  deleteAttendanceDevice: (id: string) =>
    api.delete(`/hrm/attendance/devices/${id}`).then((r) => r.data),
  regenerateDeviceKey: (id: string) =>
    api.post(`/hrm/attendance/devices/${id}/regenerate-key`, {}).then((r) => r.data),

  // FR-HR-03 — Leave
  listLeaveTypes: () => api.get('/hrm/leave/types').then((r) => r.data.data),
  listLeaveApplications: (params?: { status?: string; employeeId?: string; limit?: number }) =>
    api.get('/hrm/leave/applications', { params }).then((r) => r.data.data),
  getLeaveBalances: (employeeId: string, year?: number) =>
    api.get(`/hrm/leave/balances/${employeeId}`, { params: year ? { year } : {} }).then((r) => r.data),
  applyLeave: (payload: {
    employeeId?: string;
    leaveTypeId: string;
    startDate: string;
    endDate: string;
    reason: string;
    isHalfDay?: boolean;
    halfDayPeriod?: 'first_half' | 'second_half';
  }) => api.post('/hrm/leave/apply', payload).then((r) => r.data),
  approveLeave: (id: string) => api.post(`/hrm/leave/${id}/approve`).then((r) => r.data),
  rejectLeave: (id: string, reason: string) =>
    api.post(`/hrm/leave/${id}/reject`, { reason }).then((r) => r.data),
  cancelLeave: (id: string) => api.post(`/hrm/leave/${id}/cancel`).then((r) => r.data),
  runLeaveAccrual: (payload?: { periodYear?: number; leaveTypeId?: string; employeeId?: string }) =>
    api.post('/hrm/leave/balances/accrue', payload ?? {}).then((r) => r.data),

  // FR-HR-04 — Payroll
  listPayrollRuns: (params?: { status?: string; limit?: number }) =>
    api.get('/hrm/payroll/runs', { params }).then((r) => r.data.data),
  getPayrollRun: (id: string) => api.get(`/hrm/payroll/runs/${id}`).then((r) => r.data),
  createPayrollRun: (payload: {
    periodYear: number;
    periodMonth: number;
    paymentDate?: string;
    notes?: string;
  }) => api.post('/hrm/payroll/runs', payload).then((r) => r.data),
  calculatePayrollRun: (id: string, payload?: {
    includeEmployeeIds?: string[];
    excludeEmployeeIds?: string[];
  }) => api.post(`/hrm/payroll/runs/${id}/calculate`, payload ?? {}).then((r) => r.data),
  approvePayrollRun: (id: string) =>
    api.post(`/hrm/payroll/runs/${id}/approve`).then((r) => r.data),
  disbursePayrollRun: (id: string, payload?: { payAccountCode?: string; paymentDate?: string }) =>
    api.post(`/hrm/payroll/runs/${id}/disburse`, payload ?? {}).then((r) => r.data),
  getPayslip: (runId: string, employeeId: string) =>
    api.get(`/hrm/payroll/runs/${runId}/payslips/${employeeId}`).then((r) => r.data.payslip),

  // FR-HR-05 — Performance
  listCycles: (params?: { status?: string }) =>
    api.get('/hrm/performance/cycles', { params }).then((r) => r.data.data),
  createCycle: (payload: {
    code?: string; name: string; description?: string;
    startsOn: string; endsOn: string; reviewDueBy?: string;
  }) => api.post('/hrm/performance/cycles', payload).then((r) => r.data),
  updateCycle: (id: string, payload: any) =>
    api.patch(`/hrm/performance/cycles/${id}`, payload).then((r) => r.data),
  deleteCycle: (id: string) =>
    api.delete(`/hrm/performance/cycles/${id}`).then((r) => r.data),
  deleteGoal: (id: string) =>
    api.delete(`/hrm/performance/goals/${id}`).then((r) => r.data),
  deleteReview: (id: string) =>
    api.delete(`/hrm/performance/reviews/${id}`).then((r) => r.data),
  listGoals: (params?: { cycleId?: string; employeeId?: string }) =>
    api.get('/hrm/performance/goals', { params }).then((r) => r.data.data),
  createGoal: (payload: {
    cycleId: string; employeeId: string; title: string;
    description?: string; category?: string;
    weightPercent?: number; targetMetric?: string; targetValue?: string;
    dueDate?: string;
  }) => api.post('/hrm/performance/goals', payload).then((r) => r.data),
  updateGoal: (id: string, payload: any) =>
    api.patch(`/hrm/performance/goals/${id}`, payload).then((r) => r.data),
  rateGoal: (id: string, payload: { completionRating: number; completionNotes?: string }) =>
    api.post(`/hrm/performance/goals/${id}/rate`, payload).then((r) => r.data),
  listReviews: (params?: { cycleId?: string; employeeId?: string }) =>
    api.get('/hrm/performance/reviews', { params }).then((r) => r.data.data),
  submitReview: (payload: {
    cycleId: string; employeeId: string;
    reviewerType: 'self' | 'manager' | 'peer';
    overallRating: number;
    strengths?: string; improvements?: string;
    goalsForNextCycle?: string; comments?: string;
  }) => api.post('/hrm/performance/reviews', payload).then((r) => r.data),

  // FR-HR-06 — Recruitment
  listJobs: (params?: { status?: string }) =>
    api.get('/hrm/recruitment/jobs', { params }).then((r) => r.data.data),
  getJob: (id: string) => api.get(`/hrm/recruitment/jobs/${id}`).then((r) => r.data),
  createJob: (payload: {
    code?: string; title: string; departmentId?: string;
    description?: string; responsibilities?: string; requirements?: string;
    employmentType?: 'full_time'|'part_time'|'contract'|'intern';
    location?: string; remote?: boolean;
    salaryMin?: number; salaryMax?: number;
    headcount?: number; hiringManagerId?: string;
    publishNow?: boolean;
  }) => api.post('/hrm/recruitment/jobs', payload).then((r) => r.data),
  updateJob: (id: string, payload: any) =>
    api.patch(`/hrm/recruitment/jobs/${id}`, payload).then((r) => r.data),
  deleteJob: (id: string) =>
    api.delete(`/hrm/recruitment/jobs/${id}`).then((r) => r.data),
  updateCandidate: (id: string, payload: any) =>
    api.patch(`/hrm/recruitment/candidates/${id}`, payload).then((r) => r.data),
  deleteCandidate: (id: string) =>
    api.delete(`/hrm/recruitment/candidates/${id}`).then((r) => r.data),
  deleteApplication: (id: string) =>
    api.delete(`/hrm/recruitment/applications/${id}`).then((r) => r.data),
  listCandidates: (params?: { q?: string; status?: string; jobPostingId?: string }) =>
    api.get('/hrm/recruitment/candidates', { params }).then((r) => r.data.data),
  getCandidate: (id: string) => api.get(`/hrm/recruitment/candidates/${id}`).then((r) => r.data),
  createCandidate: (payload: {
    fullName: string; email?: string; phone?: string;
    resumeUrl?: string; linkedinUrl?: string;
    currentCompany?: string; currentTitle?: string;
    yearsExperience?: number; source?: string; notes?: string;
    jobPostingId?: string;
  }) => api.post('/hrm/recruitment/candidates', payload).then((r) => r.data),
  listApplications: (params?: {
    jobPostingId?: string; stage?: string; assignedTo?: string; limit?: number;
  }) => api.get('/hrm/recruitment/applications', { params }).then((r) => r.data.data),
  advanceApplicationStage: (id: string, payload: {
    toStage: string; rating?: number; rejectionReason?: string; notes?: string;
  }) => api.post(`/hrm/recruitment/applications/${id}/advance`, payload).then((r) => r.data),
  listOffers: (params?: { applicationId?: string; status?: string }) =>
    api.get('/hrm/recruitment/offers', { params }).then((r) => r.data.data),
  createOffer: (payload: {
    applicationId: string; title: string; departmentId?: string;
    baseSalary: number; signingBonus?: number; benefitsSummary?: string;
    startDate?: string; expiresOn?: string; offerLetterUrl?: string;
  }) => api.post('/hrm/recruitment/offers', payload).then((r) => r.data),
  sendOffer: (id: string) =>
    api.post(`/hrm/recruitment/offers/${id}/send`).then((r) => r.data),
  respondOffer: (id: string, accept: boolean, declineReason?: string) =>
    api.post(`/hrm/recruitment/offers/${id}/respond`, { accept, declineReason }).then((r) => r.data),
  onboardCandidate: (id: string, payload: {
    employeeCode: string; hireDate: string;
    designation?: string; departmentId?: string;
    contractType?: 'full_time'|'part_time'|'contract'|'intern';
    baseSalary?: number;
    payFrequency?: 'monthly'|'bi_weekly'|'weekly';
    reportsToId?: string; probationEndDate?: string;
  }) => api.post(`/hrm/recruitment/candidates/${id}/onboard`, payload).then((r) => r.data),

  // FR-HR-07 — Training
  listCourses: (params?: { status?: string; category?: string }) =>
    api.get('/hrm/training/courses', { params }).then((r) => r.data.data),
  getCourse: (id: string) => api.get(`/hrm/training/courses/${id}`).then((r) => r.data),
  createCourse: (payload: {
    code?: string; title: string; description?: string;
    category?: 'compliance'|'technical'|'leadership'|'onboarding'|'other';
    provider?: string;
    deliveryMode?: 'online'|'classroom'|'self_paced'|'blended';
    durationHours?: number;
    passingScore?: number;
    isMandatory?: boolean;
    costPerSeat?: number;
    externalUrl?: string;
  }) => api.post('/hrm/training/courses', payload).then((r) => r.data),
  updateCourse: (id: string, payload: any) =>
    api.patch(`/hrm/training/courses/${id}`, payload).then((r) => r.data),
  deleteCourse: (id: string) =>
    api.delete(`/hrm/training/courses/${id}`).then((r) => r.data),
  deleteAssignment: (id: string) =>
    api.delete(`/hrm/training/assignments/${id}`).then((r) => r.data),
  listAssignments: (params?: { employeeId?: string; courseId?: string; status?: string }) =>
    api.get('/hrm/training/assignments', { params }).then((r) => r.data.data),
  assignCourse: (payload: {
    courseId: string; employeeIds: string[];
    dueDate?: string; notes?: string;
  }) => api.post('/hrm/training/assignments', payload).then((r) => r.data),
  startAssignment: (id: string) =>
    api.post(`/hrm/training/assignments/${id}/start`).then((r) => r.data),
  completeAssignment: (id: string, payload?: {
    score?: number; certificateUrl?: string; notes?: string;
  }) => api.post(`/hrm/training/assignments/${id}/complete`, payload ?? {}).then((r) => r.data),
  cancelAssignment: (id: string, reason?: string) =>
    api.post(`/hrm/training/assignments/${id}/cancel`, { reason }).then((r) => r.data),
  getTrainingCompliance: () =>
    api.get('/hrm/training/compliance').then((r) => r.data.data),
};

export const adminApi = {
  listRoles: () => api.get('/admin/roles').then((r) => r.data.data),
  getRole: (id: string) => api.get(`/admin/roles/${id}`).then((r) => r.data),
  getRolePermissions: (id: string) =>
    api.get(`/admin/roles/${id}/permissions`).then((r) => r.data.data),
  createRole: (payload: {
    code: string;
    name: string;
    description?: string | null;
    permissionCodes?: string[];
  }) => api.post('/admin/roles', payload).then((r) => r.data),
  updateRole: (id: string, payload: { name?: string; description?: string | null }) =>
    api.patch(`/admin/roles/${id}`, payload).then((r) => r.data),
  deleteRole: (id: string) =>
    api.delete(`/admin/roles/${id}`).then((r) => r.data),
  // Full replace shape: { permissionCodes: [...] }
  // Delta shape:        { addedCodes: [...], removedCodes: [...] }
  updateRolePermissions: (
    id: string,
    payload: { permissionCodes: string[] } | { addedCodes: string[]; removedCodes: string[] }
  ) => api.put(`/admin/roles/${id}/permissions`, payload).then((r) => r.data),
  listPermissions: () => api.get('/admin/permissions').then((r) => r.data.data),
  getSettings: () => api.get('/admin/settings').then((r) => r.data),
  patchSettings: (payload: any) => api.patch('/admin/settings', payload).then((r) => r.data),
  // Document numbering: hard-reserve a number when a form opens, release on cancel.
  reserveNumber: (docType: string) =>
    api.post('/admin/numbering/reserve', { docType }).then((r) => r.data),
  releaseNumber: (reservationId: string) =>
    api.post('/admin/numbering/release', { reservationId }).then((r) => r.data),
  listDocumentSequences: () => api.get('/admin/document-sequences').then((r) => r.data.data),
  patchDocumentSequence: (id: string, payload: {
    prefix?: string; suffix?: string; padding_length?: number; reset_frequency?: string;
  }) => api.patch(`/admin/document-sequences/${id}`, payload).then((r) => r.data),

  // FR-ADMIN-03.01-03 Integrations
  listIntegrations: () => api.get('/admin/integrations').then((r) => r.data.data),
  getIntegration: (id: string) => api.get(`/admin/integrations/${id}`).then((r) => r.data),
  createIntegration: (payload: {
    provider: string; name: string; credentials?: any; config?: any;
  }) => api.post('/admin/integrations', payload).then((r) => r.data),
  updateIntegration: (id: string, payload: any) =>
    api.patch(`/admin/integrations/${id}`, payload).then((r) => r.data),
  deleteIntegration: (id: string) =>
    api.delete(`/admin/integrations/${id}`).then((r) => r.data),
  testIntegration: (provider: string, payload?: { credentials?: any; integrationId?: string }) =>
    api.post(`/admin/integrations/test/${provider}`, payload ?? {}).then((r) => r.data),

  // FR-ADMIN-03.06-08 Backups
  listBackups: (limit = 20) =>
    api.get('/admin/backup/status', { params: { limit } }).then((r) => r.data.data),
  getBackupStatus: (id: string) =>
    api.get(`/admin/backup/status/${id}`).then((r) => r.data),
  triggerBackup: (payload?: {
    backupType?: 'full'|'schema'|'tenant_only'; notes?: string;
  }) => api.post('/admin/backup/trigger', payload ?? {}).then((r) => r.data),
  getRestoreInstructions: (backupId: string) =>
    api.post('/admin/backup/restore', { backupId }).then((r) => r.data),

  // FR-ADMIN-03.09-10 GDPR
  listDataRequests: (params?: { status?: string }) =>
    api.get('/admin/data-requests', { params }).then((r) => r.data.data),
  createDataExport: (payload: {
    subjectId: string; subjectType?: 'customer'|'employee'|'user'; reason?: string;
  }) => api.post('/admin/data-exports', payload).then((r) => r.data),
  createDataDeletion: (payload: {
    subjectId: string; subjectType?: 'customer'|'employee'|'user';
    mode?: 'anonymize'|'hard'; reason?: string;
  }) => api.post('/admin/data-deletion', payload).then((r) => r.data),
  processDataRequest: (id: string) =>
    api.post(`/admin/data-requests/${id}/process`).then((r) => r.data),

  // FR-ADMIN-04 Notifications
  listNotificationTemplates: (params?: { channel?: string }) =>
    api.get('/admin/notifications/templates', { params }).then((r) => r.data.data),
  getNotificationTemplate: (code: string, channel: string = 'email') =>
    api.get(`/admin/notifications/templates/${code}`, { params: { channel } }).then((r) => r.data),
  updateNotificationTemplate: (code: string, channel: string = 'email', payload: any) =>
    api.put(`/admin/notifications/templates/${code}`, payload, { params: { channel } }).then((r) => r.data),
  testNotification: (payload: {
    templateCode: string; channel?: 'email'|'sms'|'push'|'in_app';
    recipient: string; mergeValues?: Record<string, string>;
  }) => api.post('/admin/notifications/test', payload).then((r) => r.data),
  listNotificationLog: (limit = 100) =>
    api.get('/admin/notifications/log', { params: { limit } }).then((r) => r.data.data),

  // FR-ADMIN-05 Feature flags
  listFeatureFlags: () => api.get('/admin/feature-flags').then((r) => r.data.data),
  createFeatureFlag: (payload: {
    code: string; name: string; description?: string;
    isEnabled?: boolean; rolloutPercentage?: number; config?: any;
  }) => api.post('/admin/feature-flags', payload).then((r) => r.data),
  toggleFeatureFlag: (code: string, payload: {
    isEnabled?: boolean; rolloutPercentage?: number; config?: any;
  }) => api.patch(`/admin/feature-flags/${code}`, payload).then((r) => r.data),

  // FR-ADMIN-06 API keys + webhooks
  listApiKeys: () => api.get('/admin/api-keys').then((r) => r.data.data),
  createApiKey: (payload: { name: string; scopes: string[]; expiresAt?: string }) =>
    api.post('/admin/api-keys', payload).then((r) => r.data),
  revokeApiKey: (id: string) =>
    api.delete(`/admin/api-keys/${id}`).then((r) => r.data),
  listWebhooks: () => api.get('/admin/webhooks').then((r) => r.data.data),
  createWebhook: (payload: {
    name: string; url: string; events: string[]; retryCount?: number;
  }) => api.post('/admin/webhooks', payload).then((r) => r.data),
  updateWebhook: (id: string, payload: any) =>
    api.patch(`/admin/webhooks/${id}`, payload).then((r) => r.data),
  deleteWebhook: (id: string) =>
    api.delete(`/admin/webhooks/${id}`).then((r) => r.data),
  listWebhookDeliveries: (id: string, limit = 50) =>
    api.get(`/admin/webhooks/${id}/deliveries`, { params: { limit } }).then((r) => r.data.data),
  testWebhook: (id: string) =>
    api.post(`/admin/webhooks/${id}/test`).then((r) => r.data),
};

export const usersApi = {
  list: (params?: { q?: string; page?: number; pageSize?: number }) =>
    api.get('/users', { params }).then((r) => r.data),
  get: (id: string) => api.get(`/users/${id}`).then((r) => r.data),
  create: (payload: any) => api.post('/users', payload).then((r) => r.data),
  update: (id: string, payload: any) => api.patch(`/users/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/users/${id}`).then((r) => r.data),
};

export const branchesApi = {
  list: () => api.get('/branches').then((r) => r.data.data),
  get: (id: string) => api.get(`/branches/${id}`).then((r) => r.data),
  create: (payload: any) => api.post('/branches', payload).then((r) => r.data),
  update: (id: string, payload: any) => api.patch(`/branches/${id}`, payload).then((r) => r.data),
};

export const reportsApi = {
  dashboard: () => api.get('/reports/dashboard').then((r) => r.data),

  // Sales reports (FR-REP-01)
  salesDaily: (params: { startDate: string; endDate: string; branchId?: string }) =>
    api.get('/reports/sales/daily', { params }).then((r) => r.data),
  salesByCashier: (params: { startDate: string; endDate: string; branchId?: string }) =>
    api.get('/reports/sales/by-cashier', { params }).then((r) => r.data),
  salesByCategory: (params: { startDate: string; endDate: string; branchId?: string }) =>
    api.get('/reports/sales/by-category', { params }).then((r) => r.data),
  topCustomers: (params: { startDate: string; endDate: string; limit?: number; branchId?: string }) =>
    api.get('/reports/sales/by-customer', { params }).then((r) => r.data),
  basketAnalysis: (params: { startDate: string; endDate: string; minCoOccurrence?: number; branchId?: string }) =>
    api.get('/reports/sales/basket-analysis', { params }).then((r) => r.data),

  // Inventory reports (FR-REP-02)
  inventoryValuation: (params?: { asOfDate?: string; warehouseId?: string }) =>
    api.get('/reports/inventory/valuation', { params }).then((r) => r.data),
  inventoryAging: (params?: { buckets?: string; warehouseId?: string }) =>
    api.get('/reports/inventory/aging', { params }).then((r) => r.data),
  inventoryTurnover: (params: { startDate: string; endDate: string }) =>
    api.get('/reports/inventory/turnover', { params }).then((r) => r.data),
  inventoryAbcAnalysis: (params: { startDate: string; endDate: string }) =>
    api.get('/reports/inventory/abc-analysis', { params }).then((r) => r.data),

  // Finance reports (FR-REP-03)
  financePnl: (params: { startDate: string; endDate: string }) =>
    api.get('/reports/finance/pnl', { params }).then((r) => r.data),
  financeBalanceSheet: (params?: { asOfDate?: string }) =>
    api.get('/reports/finance/balance-sheet', { params }).then((r) => r.data),
  financeCashFlow: (params: { startDate: string; endDate: string }) =>
    api.get('/reports/finance/cash-flow', { params }).then((r) => r.data),
  financeApAging: (params?: { asOfDate?: string; buckets?: string }) =>
    api.get('/reports/finance/ap-aging', { params }).then((r) => r.data),
  financeArAging: (params?: { asOfDate?: string; buckets?: string }) =>
    api.get('/reports/finance/ar-aging', { params }).then((r) => r.data),

  // HR reports (FR-REP-04)
  hrHeadcount: (params?: { asOfDate?: string; branchId?: string }) =>
    api.get('/reports/hr/headcount', { params }).then((r) => r.data),
  hrTurnover: (params: { startDate: string; endDate: string }) =>
    api.get('/reports/hr/turnover', { params }).then((r) => r.data),
  hrAttendanceSummary: (params: { startDate: string; endDate: string; departmentId?: string }) =>
    api.get('/reports/hr/attendance-summary', { params }).then((r) => r.data),
  hrPayrollRegister: (params?: { runId?: string; periodYear?: number; periodMonth?: number }) =>
    api.get('/reports/hr/payroll-register', { params }).then((r) => r.data),

  // Exports (FR-REP-05)
  exportUrl: (format: 'csv'|'xlsx'|'pdf', type: string, params: Record<string, any> = {}) => {
    const qs = new URLSearchParams({ type, ...Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)])
    ) });
    return `/api/reports/exports/${format}?${qs.toString()}`;
  },
  downloadExport: async (format: 'csv'|'xlsx'|'pdf', type: string, params: Record<string, any> = {}) => {
    const resp = await api.get(`/reports/exports/${format}`, { params: { type, ...params }, responseType: 'blob' });
    const blob = new Blob([resp.data], {
      type: format === 'csv' ? 'text/csv'
        : format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type.replace(/\./g, '_')}_${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  listExportTypes: () => api.get('/reports/exports/types').then((r) => r.data.data),

  // Schedules (FR-REP-06)
  listSchedules: () => api.get('/reports/schedule').then((r) => r.data.data),
  createSchedule: (payload: {
    name: string; reportType: string; reportParams?: any;
    exportFormat?: 'csv'|'xlsx'|'pdf';
    recipientEmails: string[];
    frequency: 'daily'|'weekly'|'monthly';
    dayOfWeek?: number; dayOfMonth?: number;
    timeOfDay?: string; timezone?: string;
  }) => api.post('/reports/schedule', payload).then((r) => r.data),
  updateSchedule: (id: string, payload: any) =>
    api.patch(`/reports/schedule/${id}`, payload).then((r) => r.data),
  deleteSchedule: (id: string) =>
    api.delete(`/reports/schedule/${id}`).then((r) => r.data),
};

export const auditApi = {
  listLog: (params?: { entityType?: string; entityId?: string; userId?: string; action?: string; limit?: number }) =>
    api.get('/audit/log', { params }).then((r) => r.data.data),
};

export const ecommerceApi = {
  // Storefronts
  listStorefronts: () => api.get('/ecommerce/storefronts').then((r) => r.data.data),
  getStorefront: (id: string) => api.get(`/ecommerce/storefronts/${id}`).then((r) => r.data),
  createStorefront: (payload: {
    code: string; name: string; domain?: string; theme?: string;
    currencyCode?: string; timezone?: string;
    metaTitle?: string; metaDescription?: string;
    logoUrl?: string; primaryColor?: string; config?: any;
  }) => api.post('/ecommerce/storefronts', payload).then((r) => r.data),
  updateStorefront: (id: string, payload: any) =>
    api.patch(`/ecommerce/storefronts/${id}`, payload).then((r) => r.data),

  // Catalog
  publishCatalog: (storefrontId: string, payload?: {
    productIds?: string[]; categoryIds?: string[];
    overwrite?: boolean; removeMissing?: boolean;
  }) => api.post(`/ecommerce/storefronts/${storefrontId}/publish-catalog`, payload ?? {}).then((r) => r.data),
  getCatalog: (storefrontId: string, params?: { search?: string; visibility?: string; limit?: number }) =>
    api.get(`/ecommerce/storefronts/${storefrontId}/catalog`, { params }).then((r) => r.data.data),

  // Collections
  listCollections: (storefrontId: string, params?: { status?: string }) =>
    api.get(`/ecommerce/storefronts/${storefrontId}/collections`, { params }).then((r) => r.data.data),
  getCollection: (id: string) => api.get(`/ecommerce/collections/${id}`).then((r) => r.data),
  createCollection: (storefrontId: string, payload: {
    code: string; name: string; description?: string; slug: string;
    collectionType?: 'manual' | 'smart';
    rules?: { category_ids?: string[]; brand_ids?: string[]; price_min?: number; price_max?: number };
    imageUrl?: string; displayOrder?: number;
    metaTitle?: string; metaDescription?: string;
    productIds?: string[];
  }) => api.post(`/ecommerce/storefronts/${storefrontId}/collections`, payload).then((r) => r.data),
  updateCollectionProducts: (collectionId: string, productIds: string[]) =>
    api.put(`/ecommerce/collections/${collectionId}/products`, { productIds }).then((r) => r.data),

  // Pages (FR-ECOM-03)
  listPages: (storefrontId: string, params?: { status?: string }) =>
    api.get(`/ecommerce/storefronts/${storefrontId}/pages`, { params }).then((r) => r.data.data),
  getPage: (id: string) => api.get(`/ecommerce/pages/${id}`).then((r) => r.data),
  createPage: (storefrontId: string, payload: {
    slug: string; title: string;
    pageType?: 'content'|'landing'|'about'|'contact'|'policy'|'blog';
    metaTitle?: string; metaDescription?: string;
    heroImageUrl?: string;
    blocks?: any[];
    showInNav?: boolean; displayOrder?: number;
  }) => api.post(`/ecommerce/storefronts/${storefrontId}/pages`, payload).then((r) => r.data),
  updatePage: (id: string, payload: any) =>
    api.patch(`/ecommerce/pages/${id}`, payload).then((r) => r.data),
  publishPage: (storefrontId: string, pageId: string) =>
    api.post(`/ecommerce/storefronts/${storefrontId}/pages/${pageId}/publish`).then((r) => r.data),
  unpublishPage: (id: string) =>
    api.post(`/ecommerce/pages/${id}/unpublish`).then((r) => r.data),
  deletePage: (id: string) =>
    api.delete(`/ecommerce/pages/${id}`).then((r) => r.data),

  // Reviews (FR-ECOM-05)
  listReviews: (params?: {
    storefrontId?: string; productId?: string; status?: string; limit?: number;
  }) => api.get('/ecommerce/reviews', { params }).then((r) => r.data.data),
  submitReview: (payload: {
    storefrontId: string; productId: string; customerId?: string;
    rating: number; title?: string; body: string;
    reviewerName?: string; reviewerEmail?: string;
  }) => api.post('/ecommerce/reviews', payload).then((r) => r.data),
  approveReview: (id: string) =>
    api.post(`/ecommerce/reviews/${id}/approve`).then((r) => r.data),
  rejectReview: (id: string, rejectionReason: string) =>
    api.post(`/ecommerce/reviews/${id}/reject`, { approve: false, rejectionReason }).then((r) => r.data),
  getProductReviews: (storefrontId: string, productId: string) =>
    api.get(`/ecommerce/storefronts/${storefrontId}/products/${productId}/reviews`).then((r) => r.data),

  // Checkout / Carts (FR-ECOM-04)
  listCarts: (params?: {
    storefrontId?: string; status?: string; abandoned?: boolean; limit?: number;
  }) => api.get('/ecommerce/carts', { params }).then((r) => r.data.data),
  getCart: (id: string) => api.get(`/ecommerce/carts/${id}`).then((r) => r.data),
  startCheckout: (payload: {
    storefrontId: string; customerId?: string; sessionToken?: string; email?: string;
    items: Array<{ productId: string; quantity: number; unitPrice: number; productName?: string; sku?: string }>;
    shippingAddress?: any; billingAddress?: any;
    shippingTotal?: number; taxTotal?: number;
    appliedDiscountCode?: string; notes?: string;
  }) => api.post('/ecommerce/checkout/start', payload).then((r) => r.data),
  completeCheckout: (cartId: string, payload?: { paymentReference?: string; paymentMethod?: string }) =>
    api.post(`/ecommerce/checkout/${cartId}/complete`, payload ?? {}).then((r) => r.data),
  abandonCart: (cartId: string, payload?: { reason?: string; sendRecoveryEmail?: boolean }) =>
    api.post(`/ecommerce/checkout/${cartId}/abandon`, payload ?? {}).then((r) => r.data),
  markStaleCartsAbandoned: (olderThanMinutes: number = 60) =>
    api.post('/ecommerce/checkout/mark-stale-abandoned', { olderThanMinutes }).then((r) => r.data),

  // SEO (FR-ECOM-06)
  getSitemapUrl: (storefrontId: string) => `/ecommerce/storefronts/${storefrontId}/sitemap.xml`,
  fetchSitemap: (storefrontId: string) =>
    api.get(`/ecommerce/storefronts/${storefrontId}/sitemap.xml`, { responseType: 'text' as any })
      .then((r) => r.data),
  listRedirects: (params?: { storefrontId?: string; isActive?: boolean }) =>
    api.get('/ecommerce/seo/redirects', { params }).then((r) => r.data.data),
  createRedirect: (payload: {
    storefrontId: string; fromPath: string; toPath: string;
    redirectType?: 301 | 302; isActive?: boolean; notes?: string;
  }) => api.post('/ecommerce/seo/redirects', payload).then((r) => r.data),
  updateRedirect: (id: string, payload: any) =>
    api.patch(`/ecommerce/seo/redirects/${id}`, payload).then((r) => r.data),
  deleteRedirect: (id: string) =>
    api.delete(`/ecommerce/seo/redirects/${id}`).then((r) => r.data),

  // Integrations (FR-ECOM-07)
  listConnectors: (params?: { platform?: string }) =>
    api.get('/ecommerce/integrations/connectors', { params }).then((r) => r.data.data),
  getConnector: (id: string) =>
    api.get(`/ecommerce/integrations/connectors/${id}`).then((r) => r.data),
  createConnector: (payload: {
    storefrontId?: string; platform: 'shopify'|'amazon'|'ebay'; name: string;
    credentials?: any; config?: any;
  }) => api.post('/ecommerce/integrations/connectors', payload).then((r) => r.data),
  updateConnector: (id: string, payload: any) =>
    api.patch(`/ecommerce/integrations/connectors/${id}`, payload).then((r) => r.data),
  deleteConnector: (id: string) =>
    api.delete(`/ecommerce/integrations/connectors/${id}`).then((r) => r.data),
  triggerSync: (platform: 'shopify'|'amazon'|'ebay', connectorId: string, payload?: {
    jobType?: 'inventory'|'catalog'|'orders_pull'|'price';
    productIds?: string[];
  }) => api.post(`/ecommerce/integrations/${platform}/sync/${connectorId}`, payload ?? {}).then((r) => r.data),
  listSyncJobs: (params?: { connectorId?: string; status?: string; limit?: number }) =>
    api.get('/ecommerce/integrations/sync-jobs', { params }).then((r) => r.data.data),

  // Campaigns (FR-ECOM-08)
  listCampaigns: (params?: { status?: string; limit?: number }) =>
    api.get('/ecommerce/campaigns', { params }).then((r) => r.data.data),
  getCampaign: (id: string) => api.get(`/ecommerce/campaigns/${id}`).then((r) => r.data),
  createCampaign: (payload: {
    storefrontId?: string; name: string; subject: string;
    fromName?: string; fromEmail?: string; previewText?: string;
    bodyHtml: string; bodyText?: string;
    audienceType?: 'all_customers'|'abandoned_cart'|'specific_customers'|'segment';
    audienceConfig?: any;
    scheduledAt?: string;
  }) => api.post('/ecommerce/campaigns', payload).then((r) => r.data),
  updateCampaign: (id: string, payload: any) =>
    api.patch(`/ecommerce/campaigns/${id}`, payload).then((r) => r.data),
  deleteCampaign: (id: string) =>
    api.delete(`/ecommerce/campaigns/${id}`).then((r) => r.data),
  sendCampaign: (id: string, payload?: { testOnly?: boolean; testEmails?: string[] }) =>
    api.post(`/ecommerce/campaigns/${id}/send`, payload ?? {}).then((r) => r.data),
  getCampaignStats: (id: string) =>
    api.get(`/ecommerce/campaigns/${id}/stats`).then((r) => r.data),
  recordCampaignEvent: (id: string, payload: {
    email: string; eventType: 'open'|'click'|'bounce'|'unsubscribe'|'complaint';
    url?: string; metadata?: any;
  }) => api.post(`/ecommerce/campaigns/${id}/events`, payload).then((r) => r.data),
};

export const tenantsApi = {
  getCurrent: () => api.get('/tenants/current').then((r) => r.data),
  getSettings: () => api.get('/tenants/settings').then((r) => r.data),
};

export const billingApi = {
  overview: () => api.get('/billing').then((r) => r.data),
  plans: () => api.get('/billing/plans').then((r) => r.data),
  subscribe: (plan: string, billing_cycle: 'monthly' | 'yearly') =>
    api.post('/billing/subscribe', { plan, billing_cycle }).then((r) => r.data),
  cancel: () => api.post('/billing/cancel', {}).then((r) => r.data),
  adminListPlans: () => api.get('/billing/admin/plans').then((r) => r.data.plans),
  adminUpdatePlan: (id: string, patch: any) =>
    api.patch(`/billing/admin/plans/${id}`, patch).then((r) => r.data),
  adminCreatePlan: (payload: any) =>
    api.post('/billing/admin/plans', payload).then((r) => r.data),
};
